import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { useEnvironment, useGLTF, Instances, Instance } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import { useControls, folder } from "leva";
import useCustomizerStore from "../../../state/customizer";
import useTransitionStore from "../../../state/transitions";
import useFogControlStore from "../../../state/fogControls";
import * as three from "three";
import dayHdr from "../../../assets/hdr/day-xr-tunnel-env.hdr";
import nightHdr from "../../../assets/images/Refine4.hdr";
import { vertexShader } from "../../../shaders/Lightning/vertexShader";
import { fragmentShader } from "../../../shaders/Lightning/fragmentShader";
import { useRendererEnvironment } from "../../../util/useRendererEnvironment";

import { applyXrPowertrainRendering } from "../../experience/xr/powertrainRendering";
import { CAR_ONLY_LIGHTING } from "../../experience/xr/carOnlyLighting";

const CAR_ENV_TRANSITION_DURATION = 3;

export default React.memo(function Model({ modelIndex, xrLighting = false, carOnly = false, roomLighting, displayModeOverride }) {
  const { nodes, materials } = useGLTF("/models/toyotaCrown-transformed.glb");
  const gl = useThree((state) => state.gl);
  const clonedMaterialMap = useMemo(() => {
    const map = new Map();
    const register = (material) => {
      if (!material || map.has(material.uuid)) return;
      map.set(material.uuid, material.clone());
    };

    Object.values(materials).forEach(register);
    Object.values(nodes).forEach((node) => register(node?.material));

    return map;
  }, [materials, nodes]);
  const getClonedMaterial = useCallback(
    (material) => clonedMaterialMap.get(material?.uuid) ?? material,
    [clonedMaterialMap]
  );
  const wheelArrayGeo = useMemo(
    () => [
      nodes.wheel_Standard.geometry,
      nodes.wheel_OptionA.geometry,
      nodes.wheel_Platinum.geometry,
    ],
    [nodes]
  );
  const wheelArrayMat = useMemo(
    () => [
      getClonedMaterial(nodes.wheel_Standard.material),
      getClonedMaterial(nodes.wheel_OptionA.material),
      getClonedMaterial(nodes.wheel_Platinum.material),
    ],
    [getClonedMaterial, nodes]
  );
  const tireArrayGeo = useMemo(
    () => [nodes.tire_19.geometry, nodes.tire_21.geometry],
    [nodes]
  );
  const tireArrayMat = useMemo(
    () => [
      getClonedMaterial(materials.tire_19_mat),
      getClonedMaterial(materials.tire_21_mat),
    ],
    [getClonedMaterial, materials]
  );
  const model = useCustomizerStore((s) => s.model);
  const trimColor = useCustomizerStore((s) => s.trimColor);
  const selectedWheelIndex = useCustomizerStore(
    (s) => s.selectionByModel[modelIndex]?.wheel ?? 0
  );
  const desktopDisplayMode = useTransitionStore((s) => s.DisplayMode);
  const DisplayMode = displayModeOverride ?? desktopDisplayMode;
  const DayMode = useTransitionStore((s) => s.DayMode);
  const SuspensionMode = useTransitionStore((s) => s.SuspensionMode);
  const carReflections = useFogControlStore((state) => state.carReflections);
  const at21WheelMaterial = useFogControlStore((state) => state.at21WheelMaterial);
  const daySource = useEnvironment({ files: dayHdr });
  const nightSource = useEnvironment({ files: nightHdr });
  const dayEnvironmentTexture = useRendererEnvironment(daySource, xrLighting);
  const nightEnvironmentTexture = useRendererEnvironment(nightSource, xrLighting);

  const wheelX = 0.78;
  const wheelY = 0.38;
  const wheelZ = 1.43;

  const group = useRef();
  const trimGroup = useRef();
  const trim = useRef();
  const roofXLE = useRef();
  const roofPlatLim = useRef();
  const bitone = useRef();
  const constantTrim = useRef();
  const glass = useRef();
  const windows = useRef();
  const envTransitionRef = useRef({
    active: false,
    elapsed: 0,
    currentMode:
      DayMode === "day" && DisplayMode !== "flipper" && !SuspensionMode
        ? "day"
        : "night",
  });

  useEffect(() => {
    if (xrLighting && DisplayMode === "powertrain") {
      return applyXrPowertrainRendering(group.current);
    }
  }, [xrLighting, DisplayMode]);

  const getEnvironmentSettings = useCallback(
    (mode) => ({
      texture: mode === "day" ? dayEnvironmentTexture : nightEnvironmentTexture,
      intensity:
        mode === "day"
          ? carReflections.dayXrIntensity
          : carReflections.nightFlipperIntensity,
    }),
    [
      dayEnvironmentTexture,
      nightEnvironmentTexture,
      carReflections.dayXrIntensity,
      carReflections.nightFlipperIntensity,
    ]
  );

  const getCarMaterials = useCallback(() => {
    if (!group.current) {
      return [
        ...new Map(
          [...wheelArrayMat, ...tireArrayMat]
            .filter(Boolean)
            .map((material) => [material.uuid, material])
        ).values(),
      ];
    }

    const materials = new Map();

    group.current.traverse((child) => {
      const material = child.material;
      if (!material) return;

      const list = Array.isArray(material) ? material : [material];
      list.forEach((item) => {
        if ("envMapIntensity" in item) {
          materials.set(item.uuid, item);
        }
      });
    });

    [...wheelArrayMat, ...tireArrayMat].forEach((material) => {
      if (material && "envMapIntensity" in material) {
        materials.set(material.uuid, material);
      }
    });

    return [...materials.values()];
  }, [wheelArrayMat, tireArrayMat]);

  const applyCarEnvironment = useCallback(
    (texture, multiplier) => {
      getCarMaterials().forEach((material) => {
        const baseIntensity =
          material.userData.baseCarEnvIntensity ??
          material.envMapIntensity ??
          1;

        material.userData.baseCarEnvIntensity = baseIntensity;
        const changed = material.envMap !== texture;
        material.envMap = texture;
        material.envMapIntensity = baseIntensity * multiplier;
        if (changed) material.needsUpdate = true;
      });
    },
    [getCarMaterials]
  );

  useEffect(() => {
    return () => {
      clonedMaterialMap.forEach((material) => material.dispose?.());
    };
  }, [clonedMaterialMap]);

  useEffect(() => {
    const maxAnisotropy = gl.capabilities.getMaxAnisotropy();
    const textureKeys = [
      "map",
      "normalMap",
      "roughnessMap",
      "metalnessMap",
      "aoMap",
      "emissiveMap",
      "alphaMap",
    ];

    clonedMaterialMap.forEach((material) => {
      textureKeys.forEach((key) => {
        const texture = material[key];
        if (!texture) {
          return;
        }

        texture.anisotropy = maxAnisotropy;
        texture.needsUpdate = true;
      });

      material.needsUpdate = true;
    });
  }, [clonedMaterialMap, gl]);

  useEffect(() => {
    if (!dayEnvironmentTexture || !nightEnvironmentTexture) {
      return;
    }

    const targetMode =
      DayMode === "day" && DisplayMode !== "flipper" && !SuspensionMode
        ? "day"
        : "night";
    const targetSettings = getEnvironmentSettings(targetMode);

    if (envTransitionRef.current.currentMode === targetMode) {
      applyCarEnvironment(targetSettings.texture, targetSettings.intensity);
      return;
    }

    const sourceSettings = getEnvironmentSettings(envTransitionRef.current.currentMode);

    envTransitionRef.current = {
      active: true,
      elapsed: 0,
      currentMode: envTransitionRef.current.currentMode,
      targetMode,
      sourceIntensity: sourceSettings.intensity,
      targetIntensity: targetSettings.intensity,
    };
  }, [
    DayMode,
    DisplayMode,
    SuspensionMode,
    selectedWheelIndex,
    getEnvironmentSettings,
    applyCarEnvironment,
  ]);

  useEffect(() => {
    // console.log(`update trim`, trimColor);
    // * change trim color of selected model
    updateColor();
  }, [trimColor]);

  useEffect(() => {
    updateMaterialsBasedOnDisplayMode();
  }, [DisplayMode]);

  useEffect(() => {
    getCarMaterials().forEach((material) => {
      if (material.userData.baseCarEnvIntensity == null) {
        material.userData.baseCarEnvIntensity = material.envMapIntensity ?? 1;
      }
    });
  }, [getCarMaterials]);

  const updateColor = useCallback(() => {
    if (DisplayMode == "interior") return; // todo: interior color change logic here
    if (modelIndex > 0) {
      if (Array.isArray(trimColor[modelIndex])) {
        // * bi-tone paint logic
        // todo: add change hood color and side trim for bi-tone
        roofPlatLim.current.material.color.set(trimColor[modelIndex][1]);
        bitone.current.material.color.set(trimColor[modelIndex][1]);
        trim.current.material.color.set(trimColor[modelIndex][0]);

        // constantTrim.current.material.color.set(trimColor[modelIndex][0]);

        //todo: alpha blend windows
      } else {
        // * solid color lim/plat model logic
        roofPlatLim.current.material.color.set(trimColor[modelIndex]);
        bitone.current.material.color.set(trimColor[modelIndex]);
        trim.current.material.color.set(trimColor[modelIndex]);

        // constantTrim.current.material.color.set(trimColor[modelIndex]);
      }
    } else {
      // * xle color logic
      bitone.current.material.color.set(trimColor[modelIndex]);
      roofXLE.current.material.color.set(trimColor[modelIndex]);
      trim.current.material.color.set(trimColor[modelIndex]);

      // constantTrim.current.material.color.set(trimColor[modelIndex]);
    }
    // }
  });

  const updateMaterialsBasedOnDisplayMode = useCallback(() => {
    if (DisplayMode == "interior") return; //todo: interior color switch logic here
    if (modelIndex === model) {
      if (DisplayMode == "powertrain") {
        trimGroup.current.children.forEach((part) => {
          part.material.opacity = 0.24;
          // part.material.opacity = 0.5;
          if (part.material.transmission) part.material.transmission = 0;
          // if (part.material.transmission) part.material.transmission = 0.5;
          if (part.material.ior) part.material.ior = 2.33;
          if (part.material.sheen) part.material.sheen = 1;
          if (part.material.sheenRoughness) part.material.sheenRoughness = 1;
          if (part.material.sheenColor) part.material.sheenColor.set("#2d88ff");
          part.material.attenuationDistance = 6;
          if (part.material.specularIntensity)
            part.material.specularIntensity = 0.84;
          if (part.material.specularColor) {
            part.material.specularColor.set("#0066ff");
          }
          if (part.material.attenuationDistance) part.material.metalness = 1;
          part.material.roughness = 0;
          part.material.reflectivity = 1;
          part.material.color.set("#545251");
        });
        constantTrim.current.material.transparent = true;
        constantTrim.current.material.opacity = 0.5;
      } else if (DisplayMode == "exterior" || DisplayMode == "flipper") {
        trimGroup.current.children.forEach((part) => {
          part.material.opacity = 1;
          if (part.material.transmission) part.material.transmission = 1;
          if (part.material.ior) part.material.ior = 1.5;
          if (part.material.sheen) part.material.sheen = 0.4;
          if (part.material.sheenRoughness) part.material.sheenRoughness = 0.15;
          if (part.material.sheenColor) part.material.sheenColor.set("#fff");
          part.material.attenuationDistance = 6;
          if (part.material.specularIntensity)
            part.material.specularIntensity = 0.4;
          if (part.material.specularColor) {
            part.material.specularColor.set("#fff");
          }
          if (part.material.attenuationDistance) part.material.metalness = 0;

          part.material.metalness = 1;
          part.material.roughness = 0.5; //1
          part.material.reflectivity = 0.59;
          updateColor();
        });
        constantTrim.current.material.transparent = false;
        constantTrim.current.material.opacity = 1;
      }
    }
  });

  const { X, Y, Z } = useControls({
    wheels: folder({
      X: { value: -0.81, min: -10, max: 10, step: 0.001 },
      Y: { value: 0.38, min: -10, max: 10, step: 0.001 },
      Z: { value: -1.43, min: -10, max: 10, step: 0.001 },
    }),
  });

  const { ...props } = useControls({
    PaintShop: folder({
      metalness: { value: 1, min: 0, max: 1, step: 0.001 },
      roughness: { value: 0.5, min: 0, max: 1, step: 0.001 }, // .83
      envMapIntensity: {
        value: 1.1,
        min: 0,
        max: 5,
        step: 0.001,
      },
      reflectivity: { value: 0.14, min: 0, max: 1, step: 0.001 },
      clearcoat: { value: 1, min: 0, max: 1, step: 0.001 },
      clearcoatRoughness: { value: 0, min: 0, max: 1, step: 0.001 },
      transparent: { value: true },
      wireframe: { value: false },
      // visible: { value: true },
      fog: { value: false },
      // flatShading: { value: false },
      vertexColors: { value: false },
      sheenRoughness: { value: 0.15, min: 0, max: 1, step: 0.001 },
      thickness: { value: 8.33, min: 0, max: 10, step: 0.001 },
      ior: { value: 1.5, min: 0, max: 2.33, step: 0.001 },
      transmission: { value: 0, min: 0, max: 1, step: 0.001 },
      opacity: { value: 1, min: 0, max: 1, step: 0.001 },
      attenuationColor: { value: 1, min: 0, max: 1, step: 0.001 },
      attenuationDistance: { value: Infinity, min: 0, max: Infinity, step: 1 },
      attenuationColor: {
        value: "#ffffff",
        onChange: (val) => {
          roofPlatLim.current.material.attenuationColor.set(val);
          roofXLE.current.material.attenuationColor.set(val);
          trim.current.material.attenuationColor.set(val);
        },
      },
      sheen: { value: 0.4, min: 0, max: 1, step: 0.001 },
      sheenColor: {
        value: "#ffffff",
        onChange: (val) => {
          roofPlatLim.current.material.sheenColor.set(val);
          roofXLE.current.material.sheenColor.set(val);
          trim.current.material.sheenColor.set(val);
        },
      },

      specularIntensity: { value: 0.4, min: 0, max: 1, step: 0.001 },
      specularColor: {
        value: "#ffffff",
        onChange: (val) => {
          roofPlatLim.current.material.specularColor.set(val);
          roofXLE.current.material.specularColor.set(val);
          trim.current.material.specularColor.set(val);
        },
      },
    }),
  });

  useEffect(() => {
    const at21Wheel = wheelArrayMat[1];
    const at21Tire = tireArrayMat[1];
    if (!at21Wheel) {
      return;
    }

    const currentReflectionMultiplier =
      DayMode === "day" && DisplayMode !== "flipper" && !SuspensionMode
        ? carReflections.dayXrIntensity
        : carReflections.nightFlipperIntensity;
    const desiredBaseEnvIntensity = at21WheelMaterial.envMapIntensity;
    const desiredFinalEnvIntensity =
      desiredBaseEnvIntensity * currentReflectionMultiplier;

    at21Wheel.color.set(at21WheelMaterial.color);
    at21Wheel.metalness = at21WheelMaterial.metalness;
    at21Wheel.roughness = at21WheelMaterial.roughness;
    at21Wheel.userData.baseCarEnvIntensity = desiredBaseEnvIntensity;
    at21Wheel.envMapIntensity = desiredFinalEnvIntensity;
    at21Wheel.needsUpdate = true;

    if (at21Tire && "envMapIntensity" in at21Tire) {
      const tireBaseEnvIntensity =
        at21Tire.userData.baseCarEnvIntensity ??
        at21Tire.envMapIntensity ??
        0.15;
      at21Tire.userData.baseCarEnvIntensity = tireBaseEnvIntensity;
      at21Tire.envMapIntensity =
        tireBaseEnvIntensity * currentReflectionMultiplier;
      at21Tire.needsUpdate = true;
    }
  }, [
    DayMode,
    DisplayMode,
    SuspensionMode,
    at21WheelMaterial,
    wheelArrayMat,
    tireArrayMat,
    carReflections.dayXrIntensity,
    carReflections.nightFlipperIntensity,
  ]);

  const { visible } = useControls({
    DriveTrain: folder({
      visible: { value: true },
    }),
  });

  // useFrame(() => {
  //   points.current.material.uniforms.uTime.value =
  //     clock.getElapsedTime() - clockOffset;
  // }, -3);

  const usingCarOnlyEnvironment = useRef(false);
  useFrame((_, delta) => {
    if (xrLighting && carOnly) {
      const roomEnvironment = roomLighting?.current?.environment;
      const fallback = getEnvironmentSettings(envTransitionRef.current.currentMode);
      // An environment multiplier is an artistic gain, not headset exposure.
      // Retain a weaker HDR fallback until optional room reflections arrive.
      applyCarEnvironment(
        roomEnvironment ?? fallback.texture,
        roomEnvironment
          ? CAR_ONLY_LIGHTING.roomEnvironmentGain
          : fallback.intensity * CAR_ONLY_LIGHTING.fallbackEnvironmentGain
      );
      usingCarOnlyEnvironment.current = true;
      return;
    }
    if (usingCarOnlyEnvironment.current) {
      const settings = getEnvironmentSettings(envTransitionRef.current.currentMode);
      applyCarEnvironment(settings.texture, settings.intensity);
      usingCarOnlyEnvironment.current = false;
    }
    const transition = envTransitionRef.current;

    if (!transition.active) {
      return;
    }

    transition.elapsed += delta;
    const progress = Math.min(
      transition.elapsed / CAR_ENV_TRANSITION_DURATION,
      1
    );
    const easedProgress = three.MathUtils.smoothstep(progress, 0, 1);
    const targetSettings = getEnvironmentSettings(transition.targetMode);
    const blendedIntensity = three.MathUtils.lerp(
      transition.sourceIntensity,
      transition.targetIntensity,
      easedProgress
    );

    applyCarEnvironment(
      targetSettings.texture,
      blendedIntensity
    );

    if (progress >= 1) {
      transition.active = false;
      transition.currentMode = transition.targetMode;
    }
  });

  return (
    <group ref={group} dispose={null}>
      {/* <mesh
        visible={modelIndex > 0}
        name="roof_Black_Lim_Plat"
        geometry={nodes.roof_Black_Lim_Plat.geometry}
        material={nodes.roof_Black_Lim_Plat.material}
      /> */}

      {/* trims */}
      <group ref={trimGroup} visible={true}>
        <mesh
          // visible={false}
          ref={bitone}
          name="body_biTone"
          geometry={nodes.body_biTone.geometry}
          material={materials.body_biTone_mat}
        >
          <meshPhysicalMaterial {...props} />
        </mesh>
        <mesh
          // visible={false}
          ref={trim}
          name="body_colorChange"
          geometry={nodes.body_colorChange.geometry}
          // material={nodes.body_colorChange.material}
        >
          <meshPhysicalMaterial {...props} />
        </mesh>

        {/* roof */}

        <mesh
          ref={roofXLE}
          // visible={false}
          visible={modelIndex === 0}
          name="roof_XLE"
          geometry={nodes.roof_XLE.geometry}
          // material={nodes.roof_XLE.material}
        >
          <meshPhysicalMaterial {...props} />
        </mesh>
        <mesh
          ref={roofPlatLim}
          visible={modelIndex > 0}
          // visible={false}
          name="roof_Lim_Plat"
          geometry={nodes.roof_Lim_Plat.geometry}
          // material={nodes.roof_Lim_Plat.material}
        >
          <meshPhysicalMaterial {...props} />
        </mesh>
        <mesh
          // visible={false}
          renderOrder={2}
          ref={glass}
          visible={modelIndex > 0 && DisplayMode !== "powertrain"}
          name="roofGlass_Lim_Plat"
          geometry={nodes.roofGlass_Lim_Plat.geometry}
          material={getClonedMaterial(nodes.roofGlass_Lim_Plat.material)}
        />

        {/* drivetrain */}
        <mesh
          visible={DisplayMode == "powertrain" && model == 2}
          renderOrder={-1}
          name="xRay_HybridMax"
          geometry={nodes.xRay_HybridMax.geometry}
          material={getClonedMaterial(nodes.xRay_Hybrid_AWD.material)}
        ></mesh>
        <mesh
          visible={DisplayMode == "powertrain" && modelIndex < 2}
          renderOrder={-1}
          name="xRay_Hybrid_AWD"
          geometry={nodes.xRay_Hybrid_AWD.geometry}
          material={getClonedMaterial(nodes.xRay_Hybrid_AWD.material)}
        />
      </group>

      {/* drivetrain */}
      {/* <mesh
        visible={DisplayMode == "powertrain" && model == 2}
        renderOrder={-1}
        name="xRay_HybridMax"
        geometry={nodes.xRay_HybridMax.geometry}
        // material={nodes.xRay_Hybrid_AWD.material} //materials.xRayShader_mat
      >
        <shaderMaterial
          transparent
          // ref={material}
          fragmentShader={fragmentShader}
          vertexShader={vertexShader}
        />
      </mesh> */}

      {/* <mesh
        scale={0.1}
        visible={DisplayMode == "powertrain" && modelIndex == 2}
        name="driveTrain_HybridMax"
        geometry={nodes.driveTrain_HybridMax.geometry}
        // material={materials.driveTrain_HybridMax_mat}
      >
        
      </mesh> */}
      <mesh
        renderOrder={-2}
        visible={DisplayMode == "powertrain" && modelIndex == 2}
        name="driveTrain_HybridMax"
        geometry={nodes.driveTrain_HybridMax.geometry}
        material={getClonedMaterial(materials.driveTrain_HybridMax_mat)}
      >
        {/* <shaderMaterial
          transparent
          // uniforms={}
          // ref={material}
          fragmentShader={fragmentShader}
          vertexShader={vertexShader}
        /> */}
      </mesh>
      <mesh
        visible={DisplayMode == "powertrain" && modelIndex == 2}
        name="turbo"
        geometry={nodes.turbo.geometry}
        material={getClonedMaterial(materials.turbo_mat)}
      />

      <mesh //* new
        visible={DisplayMode == "powertrain" && modelIndex < 2}
        name="driveTrain_Hybrid_AWD"
        geometry={nodes.driveTrain_Hybrid_AWD.geometry}
        material={getClonedMaterial(materials.driveTrain_Hybrid_AWD_mat)}
      />

      <mesh
        ref={constantTrim}
        name="body_constant"
        geometry={nodes.body_constant.geometry}
        material={getClonedMaterial(nodes.body_constant.material)}
      ></mesh>

      {/* wheels */}
      <Instances
        range={4}
        // geometry={wheelArrayGeo[wheelIndex]}
        // material={wheelArrayMat[wheelIndex]}
        geometry={
          modelIndex == 0
            ? wheelArrayGeo[0]
            : modelIndex == 2
            ? wheelArrayGeo[2]
            : wheelArrayGeo[selectedWheelIndex]
        }
        material={
          modelIndex == 0
            ? wheelArrayMat[0]
            : modelIndex == 2
            ? wheelArrayMat[2]
            : wheelArrayMat[selectedWheelIndex]
        }
      >
        <group visible={true} position={[wheelX, wheelY, wheelZ]}>
          <Instance />
        </group>
        <group visible={true} position={[wheelX, wheelY, -wheelZ]}>
          <Instance />
        </group>
        <group
          visible={true}
          position={[-wheelX, wheelY, wheelZ]}
          rotation={[Math.PI, 0, Math.PI]}
        >
          <Instance />
        </group>
        <group
          visible={true}
          position={[-wheelX, wheelY, -wheelZ]}
          rotation={[Math.PI, 0, Math.PI]}
        >
          <Instance />
        </group>
      </Instances>

      {/* tires */}

      <Instances
        range={4}
        geometry={
          modelIndex == 0
            ? tireArrayGeo[0]
            : modelIndex == 2
            ? tireArrayGeo[1]
            : tireArrayGeo[selectedWheelIndex]
        }
        material={
          modelIndex == 0
            ? tireArrayMat[0]
            : modelIndex == 2
            ? tireArrayMat[1]
            : tireArrayMat[selectedWheelIndex]
        }
        // geometry={tireArrayGeo[tireIndex]}
        // material={tireArrayMat[tireIndex]}
      >
        <group visible={true} position={[wheelX, wheelY, wheelZ]}>
          <Instance />
        </group>
        <group visible={true} position={[wheelX, wheelY, -wheelZ]}>
          <Instance />
        </group>
        <group
          visible={true}
          position={[-wheelX, wheelY, wheelZ]}
          rotation={[0, 0, Math.PI]}
        >
          <Instance />
        </group>
        <group
          visible={true}
          position={[-wheelX, wheelY, -wheelZ]}
          rotation={[0, 0, Math.PI]}
        >
          <Instance />
        </group>
      </Instances>

      {/* badges */}
      <mesh
        name="badge_crownChrome"
        geometry={nodes.badge_crownChrome.geometry}
        material={getClonedMaterial(nodes.badge_crownChrome.material)}
      />
      <mesh
        visible={modelIndex === 1}
        name="badge_Limited"
        geometry={nodes.badge_Limited.geometry}
        material={getClonedMaterial(nodes.badge_Limited.material)}
      />
      <mesh
        visible={modelIndex === 2}
        name="badge_HybridMax_Platinum"
        geometry={nodes.badge_HybridMax_Platinum.geometry}
        material={getClonedMaterial(nodes.badge_HybridMax_Platinum.material)}
      />
      <mesh
        visible={modelIndex < 2}
        name="badge_AWD"
        geometry={nodes.badge_AWD.geometry}
        material={getClonedMaterial(nodes.badge_AWD.material)}
      />
      {/* Others */}
      <mesh
        // visible={DisplayMode != "powertrain" ? visible : false}
        visible={DisplayMode != "powertrain"}
        name="Interior"
        geometry={nodes.Interior.geometry}
        material={getClonedMaterial(materials.interior_mat)}
      />

      <mesh
        visible={DisplayMode != "powertrain" && modelIndex == 0}
        name="roofInterior_XLE"
        geometry={nodes.roofInterior_XLE.geometry}
        material={getClonedMaterial(nodes.roofInterior_XLE.material)}
      />

      <mesh
        name="brakes"
        geometry={nodes.brakes.geometry}
        material={getClonedMaterial(nodes.brakes.material)}
      />
      {/* glass */}
      {/* <group visible={DisplayMode == "powertrain"}> */}
      {/* windows */}
      <mesh
        // renderOrder={2}
        name="glass_A"
        geometry={nodes.glass_A.geometry}
        material={getClonedMaterial(nodes.glass_A.material)}
      ></mesh>
      <mesh
        name="glass_C"
        geometry={nodes.glass_C.geometry}
        material={getClonedMaterial(nodes.glass_C.material)}
      />
      {/* headlights */}
      <mesh
        visible={modelIndex > 0}
        name="headlights_Lim_Plat"
        geometry={nodes.headlights_Lim_Plat.geometry}
        material={getClonedMaterial(nodes.headlights_Lim_Plat.material)}
      />
      <mesh
        visible={modelIndex > 0}
        name="headlights_Lim_Plat_glass"
        geometry={nodes.headlights_Lim_Plat_glass.geometry}
        material={getClonedMaterial(nodes.headlights_Lim_Plat_glass.material)}
      />
      <mesh
        visible={modelIndex > 0}
        name="headlights_Lim_Plat_lights"
        geometry={nodes.headlights_Lim_Plat_lights.geometry}
        material={getClonedMaterial(nodes.headlights_Lim_Plat_lights.material)}
      />
      <mesh
        visible={modelIndex === 0}
        name="headlights_XLE"
        geometry={nodes.headlights_XLE.geometry}
        material={getClonedMaterial(nodes.headlights_XLE.material)}
      />
      <mesh
        visible={modelIndex === 0}
        name="headlights_XLE_glass"
        geometry={nodes.headlights_XLE_glass.geometry}
        material={getClonedMaterial(nodes.headlights_XLE_glass.material)}
      />
      <mesh
        visible={modelIndex === 0}
        name="headlights_XLE_lights"
        geometry={nodes.headlights_XLE_lights.geometry}
        material={getClonedMaterial(nodes.headlights_XLE_lights.material)}
      />
      {/* lights other */}
      <mesh
        name="lights_constant"
        geometry={nodes.lights_constant.geometry}
        material={getClonedMaterial(nodes.lights_constant.material)}
      />

      {/* wheel proxies */}

      {/* <instances.WheelProxyBL
				name="wheelProxy_BL"
				position={[0.79, 0.37, -1.42]}
			/>
			<instances.WheelProxyBL
				name="wheelProxy_BR"
				position={[-0.79, 0.37, -1.42]}
			/>
			<instances.WheelProxyBL
				name="wheelProxy_FR"
				position={[-0.79, 0.37, 1.43]}
				rotation={[Math.PI, 0, Math.PI]}
			/>
			<mesh
				name="wheelProxy_FL"
				geometry={nodes.wheelProxy_FL.geometry}
				material={nodes.wheelProxy_FL.material}
				position={[0.79, 0.37, 1.43]}
			/> */}
    </group>
  );
});

useGLTF.preload("/models/toyotaCrown-transformed.glb");
